// 기둥(바닥에서 솟는 원통): 바닥에서 위로 빠르게(0.14초) 솟고, 결이 위로 흐르며, 시간이 갈수록 위쪽부터 노이즈로 디졸브되어 사라진다.
// 디졸브 경계가 달궈진 가장자리처럼 부색으로 빛난다. 바닥 쪽은 넓게 퍼진 발(foot)이 있어 땅에서 솟는 느낌을 준다.
// 쓰는 칸: length = 높이(m), width = 반지름(m), y = 바닥 높이, ahead = 중심을 앞으로 옮기는 거리, life·color(몸통)·color2(경계·발)·intensity·seed. 드로우콜 1.
// 모양: 원통 옆면 한 겹(둘레 16칸 × 높이 3줄: 발 / 아래 10% / 꼭대기). 위로 갈수록 70%로 가늘어진다.
// Blender 메시로 바꿀 때: u = 둘레 0..1, v = 아래 0 → 위 1, 반지름 1·높이 1 단위, 원점 = 바닥 중심.
import * as THREE from 'three/webgpu';
import { clamp, mix, smoothstep, texture, uv, vec2 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { gridLayout } from './layout';
import { fxMaterial } from './material';
import { clamp01, easeOut } from './path';
import { MODULE_CAPS, type ModuleKind } from './types';

export const PILLAR_COLS = 16;
/** 줄별 높이 비율(v) · 반지름 배율 */
const ROW_V = [0, 0.1, 1] as const;
const ROW_R = [1.7, 1.05, 0.7] as const;
const SIN = new Float32Array(PILLAR_COLS + 1);
const COS = new Float32Array(PILLAR_COLS + 1);
/** 둘레 불규칙(정점마다 고정): 완벽한 원통처럼 보이지 않게 */
const WOBBLE = new Float32Array(PILLAR_COLS + 1);
for (let i = 0; i <= PILLAR_COLS; i++) {
  const a = (i / PILLAR_COLS) * Math.PI * 2;
  SIN[i] = Math.sin(a);
  COS[i] = Math.cos(a);
  WOBBLE[i] = 1 + 0.07 * Math.sin(((i % PILLAR_COLS) / PILLAR_COLS) * Math.PI * 6 + 0.7);
}

function makeMaterial(noise: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const age = ctl.x, env = ctl.y, seed = ctl.z;
  const t = t2.w;
  const u = uv().x, v = uv().y;
  // 결: 위로 흐르는 세로 줄무늬(둘레는 정수 배라 이음매가 없다)
  const n = texture(noise, vec2(u.mul(3), v.mul(0.55).sub(t.mul(1.3)).add(seed))).g;
  const n2 = texture(noise, vec2(u.mul(5).add(0.37), v.mul(0.9).sub(t.mul(2.1)))).r;
  const grain = n.mul(0.6).add(n2.mul(0.35));
  // 디졸브: 위쪽부터. 점수 = 노이즈 + (아래일수록 높음)
  const thr = smoothstep(0.2, 0.98, age).mul(1.1);
  const score = grain.mul(0.75).add(v.oneMinus().mul(0.4));
  const keep = smoothstep(thr, thr.add(0.14), score);
  const glow = clamp(thr.add(0.14).sub(score).mul(7), 0, 1).mul(keep);
  const foot = smoothstep(0.3, 0.0, v);
  const topFade = smoothstep(1.0, 0.72, v);
  const alpha = clamp(topFade.mul(grain.mul(0.8).add(0.2)).add(foot.mul(0.35)), 0, 1).mul(keep).mul(env);
  const hot = clamp(foot.mul(0.3).add(glow.mul(0.9)).add(v.oneMinus().mul(0.05)), 0, 1);
  const col = mix(t1.rgb, t2.rgb, hot).mul(t1.w).mul(0.5).mul(grain.mul(0.5).add(0.8));
  return fxMaterial(col, alpha);
}

export class PillarModule extends BatchModule {
  readonly kind: ModuleKind = 'pillar';

  constructor(noise: THREE.Texture, cap = MODULE_CAPS.pillar) {
    super(cap, gridLayout(PILLAR_COLS, ROW_V), makeMaterial(noise), 20, 'fx-pillar');
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const sin = Math.sin(s.yaw), cos = Math.cos(s.yaw);
    const cx = s.x + sin * s.ahead, cz = s.z + cos * s.ahead;
    // 솟는 높이: 처음 0.14초에 끝까지
    const H = s.length * s.scale * easeOut(clamp01(s.age / 0.14));
    const R = s.width * s.scale;
    let k = o;
    for (let j = 0; j < ROW_V.length; j++) {
      const y = s.y + H * ROW_V[j];
      const rr = R * ROW_R[j];
      for (let i = 0; i <= PILLAR_COLS; i++) {
        const r = rr * WOBBLE[i];
        out[k] = cx + SIN[i] * r; out[k + 1] = y; out[k + 2] = cz + COS[i] * r;
        k += 3;
      }
    }
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const age01 = clamp01(s.age / s.life);
    out[0] = age01;
    out[1] = Math.min(1, s.age / 0.02);
    out[2] = s.seed;
    out[3] = 0;
  }

  protected override tint2w(s: Slot): number { return s.age; }
}
